import React, { useMemo } from 'react'
import { Translate, withLocalize } from 'react-localize-redux';
import { EnhancedTable } from './EnhancedTable';
import PropTypes from 'prop-types';
import { FUNCTIONALITY, PatientStatus } from '../../constants/types';
import { formatPatients } from '../../utils/index.jsx';
import { PERMISSION } from '../investigation/share/user_roles';
import Chip from '@mui/material/Chip';

const STATUS_CONFIG = {
    [PatientStatus.ACTIVE]: { labelKey: "patient.status.active", color: "success", variant: "outlined" },
    [PatientStatus.INACTIVE]: { labelKey: "patient.status.inactive", color: "default", variant: "outlined" },
    [PatientStatus.DECEASED]: { labelKey: "patient.status.deceased", color: "error", variant: "filled" },
    [PatientStatus.ARCHIVED]: { labelKey: "patient.status.archived", color: "default", variant: "filled" },
};

function PatientStatusChip({ status, translate }) {
    const effectiveStatus = (status == null) ? PatientStatus.ACTIVE : status;
    const config = STATUS_CONFIG[effectiveStatus] || STATUS_CONFIG[PatientStatus.ACTIVE];
    const label = translate ? translate(config.labelKey) : "";
    const displayLabel = label.includes("Missing") ? (effectiveStatus === PatientStatus.DECEASED ? "Deceased" : effectiveStatus === PatientStatus.INACTIVE ? "Inactive" : effectiveStatus === PatientStatus.ARCHIVED ? "Archived" : "Active") : label;
    return (
        <Chip
            label={displayLabel}
            color={config.color}
            size="small"
            variant={config.variant}
        />
    );
}

function PatientsTable(props) {

    const rows = useMemo(
        () => formatPatients(props.patients, props.personalFields, props.activeLanguage.code, props.insurances, props.coverages),
        [props.patients, props.personalFields, props.activeLanguage.code, props.insurances, props.coverages]
    );

    const hasInpatients = props.functionalities?.includes(FUNCTIONALITY.HOSPITALIZATION);
    const hasDeceasedWorkflow = props.functionalities?.includes(FUNCTIONALITY.DECEASED_WORKFLOW);

    // Add status chip to each row (only if investigation has deceased workflow)
    const rowsWithStatus = hasDeceasedWorkflow ? rows.map(row => ({
        ...row,
        patientStatus: <PatientStatusChip status={row.status} translate={props.translate} />
    })) : rows;

    const headCells = [
        ...props.personalFields.map(pField => {
            return { id: pField.name, alignment: "left", label: !props.translate(`investigation.create.personal_data.short-fields.${pField.name}`).includes("Missing") ? props.translate(`investigation.create.personal_data.short-fields.${pField.name}`) : pField.label }
        }),
        ...(hasDeceasedWorkflow ? [{ id: "patientStatus", alignment: "left", label: props.translate("patient.status.title").includes("Missing") ? "Status" : props.translate("patient.status.title") }] : [])
    ];

    // Helper to check if a patient (by uuid) is deceased (only relevant with DECEASED_WORKFLOW)
    const isDeceased = (uuid) => {
        if (!hasDeceasedWorkflow) return false;
        const row = rows.find(r => r.id === uuid);
        return row && row.status === PatientStatus.DECEASED;
    };
    let actions = [];
    if(props.permissions.includes(PERMISSION.MEDICAL_WRITE) && props.functionalities.includes(FUNCTIONALITY.HOSPITALIZATION)){
        actions.push({
            "type" : "hospital",
            "func" : (index) => { if(!isDeceased(index)) props.hospitalizePatientCallBack(index); },
            "color" : (uuid) => isDeceased(uuid) ? "#ccc" : undefined
        })
    }
    if(props.permissions.includes(PERMISSION.VIEW_BILLING) && props.functionalities.includes(FUNCTIONALITY.BILLING)){
        actions.push({
            "type" : "billing",
            "func" : (index) => { if(!isDeceased(index)) props.billingPatientCallBack(index); },
            "color" : (uuid) => isDeceased(uuid) ? "#ccc" : undefined
        })
    }
    if(props.permissions.includes(PERMISSION.CREATE_APPOINTMENTS) && props.functionalities.includes(FUNCTIONALITY.OUTPATIENTS)){
        actions.push({
            "type" : "appointment",
            "func" : (index) => { if(!isDeceased(index)) props.selectAppointmentPatientCallBack(index); },
            "color" : (uuid) => isDeceased(uuid) ? "#ccc" : undefined
        })
    }
    if(props.permissions.includes(PERMISSION.CREATE_SURGERY_APPOINTMENTS) && props.functionalities.includes(FUNCTIONALITY.SURGERY)){
        actions.push({
            "type" : "surgery",
            "func" : (index) => { if(!isDeceased(index)) props.surgeryPatientCallBack(index); },
            "color" : (uuid) => isDeceased(uuid) ? "#ccc" : undefined
        })
    }
    return (
        <EnhancedTable noHeader noSelectable  titleTable={<Translate id="investigation.create.summary.patients" />}
            rows={rowsWithStatus} headCells={headCells}
            selectRow={uuid => props.showPatientCallBack(uuid)}
            actions={actions}
            />
    )
}

PatientsTable.propTypes = {
    patients:PropTypes.array,
    mobile:PropTypes.bool,
    personalFields:PropTypes.array,
    showPatientCallBack:PropTypes.func,
    addInfoPatientCallBack:PropTypes.func,
    hospitalizePatientCallBack:PropTypes.func,
    billingPatientCallBack:PropTypes.func,
    selectAppointmentPatientCallBack:PropTypes.func,
    surgeryPatientCallBack:PropTypes.func
}

export default withLocalize(PatientsTable)
